import { useState, useEffect, useRef } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, 
  Upload, 
  Trash2, 
  Download, 
  Calendar,
  RefreshCw,
  Loader2,
  File,
  CheckCircle,
  XCircle,
  RotateCcw,
  ScrollText,
  BookOpen,
  BarChart3,
  PieChart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDocumentsPage() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [activeTab, setActiveTab] = useState('statuten');
  
  // Form states
  const [statutenTitle, setStatutenTitle] = useState('Vereinsstatuten');
  const [statutenDescription, setStatutenDescription] = useState('');
  const statutenFileRef = useRef(null);
  
  const [protokollTitle, setProtokollTitle] = useState('');
  const [protokollYear, setProtokollYear] = useState(new Date().getFullYear());
  const [protokollDate, setProtokollDate] = useState('');
  const [protokollDescription, setProtokollDescription] = useState('');
  const protokollFileRef = useRef(null);

  // Jahresbericht form
  const [jbTitle, setJbTitle] = useState('');
  const [jbYear, setJbYear] = useState(new Date().getFullYear() - 1);
  const [jbDescription, setJbDescription] = useState('');
  const jbFileRef = useRef(null);

  // Finanzbericht form
  const [fbTitle, setFbTitle] = useState('');
  const [fbYear, setFbYear] = useState(new Date().getFullYear() - 1);
  const [fbDescription, setFbDescription] = useState('');
  const fbFileRef = useRef(null);

  // Allgemein form
  const [allgTitle, setAllgTitle] = useState('');
  const allgFileRef = useRef(null);

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    const token = getToken();
    try {
      const response = await axios.get(`${API_URL}/api/documents/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadStatuten = async (e) => {
    e.preventDefault();
    const file = statutenFileRef.current?.files[0];
    if (!file) {
      toast.error('Bitte wählen Sie eine PDF-Datei aus');
      return;
    }

    setUploading(true);
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', statutenTitle);
    formData.append('description', statutenDescription);

    try {
      await axios.post(`${API_URL}/api/documents/admin/statuten`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Statuten erfolgreich hochgeladen');
      fetchDocuments();
      statutenFileRef.current.value = '';
      setStatutenDescription('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadProtokoll = async (e) => {
    e.preventDefault();
    const file = protokollFileRef.current?.files[0];
    if (!file) {
      toast.error('Bitte wählen Sie eine PDF-Datei aus');
      return;
    }
    if (!protokollTitle || !protokollDate) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setUploading(true);
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', protokollTitle);
    formData.append('year', protokollYear.toString());
    formData.append('date', protokollDate);
    formData.append('description', protokollDescription);

    try {
      await axios.post(`${API_URL}/api/documents/admin/protokoll`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Protokoll erfolgreich hochgeladen');
      fetchDocuments();
      protokollFileRef.current.value = '';
      setProtokollTitle('');
      setProtokollDate('');
      setProtokollDescription('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadYearlyReport = async (e, type, label, titleVal, yearVal, descVal, fileRef, resetFn) => {
    e.preventDefault();
    const file = fileRef.current?.files[0];
    if (!file) {
      toast.error('Bitte wählen Sie eine PDF-Datei aus');
      return;
    }
    if (!titleVal) {
      toast.error('Bitte geben Sie einen Titel ein');
      return;
    }
    setUploading(true);
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', titleVal);
    formData.append('year', yearVal.toString());
    if (descVal) formData.append('description', descVal);

    try {
      await axios.post(`${API_URL}/api/documents/admin/${type}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${label} erfolgreich hochgeladen`);
      fetchDocuments();
      fileRef.current.value = '';
      resetFn();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadAllgemein = async (e) => {
    e.preventDefault();
    const file = allgFileRef.current?.files[0];
    if (!file || !allgTitle) {
      toast.error('Titel und PDF-Datei sind erforderlich');
      return;
    }
    setUploading(true);
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', allgTitle);
    try {
      await axios.post(`${API_URL}/api/documents/admin/allgemein`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Dokument hochgeladen');
      fetchDocuments();
      allgFileRef.current.value = '';
      setAllgTitle('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('Dokument wirklich löschen?')) return;
    
    const token = getToken();
    try {
      await axios.delete(`${API_URL}/api/documents/admin/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dokument gelöscht');
      fetchDocuments();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleRestore = async (docId) => {
    const token = getToken();
    try {
      await axios.put(`${API_URL}/api/documents/admin/${docId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dokument wiederhergestellt');
      fetchDocuments();
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const statuten = documents.filter(d => d.type === 'statuten');
  const protokolle = documents.filter(d => d.type === 'protokoll');
  const jahresberichte = documents.filter(d => d.type === 'jahresbericht');
  const finanzberichte = documents.filter(d => d.type === 'finanzbericht');
  const allgemein = documents.filter(d => d.type === 'allgemein');
  const activeStatuten = statuten.find(s => s.is_active);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wider mb-2">
            Dokumente verwalten
          </h1>
          <p className="text-muted-foreground">
            Statuten, GV-Protokolle, Jahres- und Finanzberichte hochladen und verwalten
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => setActiveTab('statuten')}
            className={`px-4 py-2 font-medium text-sm uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'statuten' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <ScrollText className="inline w-4 h-4 mr-2" />
            Statuten
          </button>
          <button
            onClick={() => setActiveTab('protokolle')}
            className={`px-4 py-2 font-medium text-sm uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'protokolle' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <BookOpen className="inline w-4 h-4 mr-2" />
            GV-Protokolle
          </button>
          <button
            onClick={() => setActiveTab('jahresberichte')}
            className={`px-4 py-2 font-medium text-sm uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'jahresberichte' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-jahresberichte"
          >
            <BarChart3 className="inline w-4 h-4 mr-2" />
            Jahresberichte
          </button>
          <button
            onClick={() => setActiveTab('finanzberichte')}
            className={`px-4 py-2 font-medium text-sm uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'finanzberichte' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-finanzberichte"
          >
            <PieChart className="inline w-4 h-4 mr-2" />
            Finanzberichte
          </button>
          <button
            onClick={() => setActiveTab('allgemein')}
            className={`px-4 py-2 font-medium text-sm uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'allgemein' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-allgemein"
          >
            <FileText className="inline w-4 h-4 mr-2" />
            Downloads
          </button>
        </div>

        {/* Statuten Tab */}
        {activeTab === 'statuten' && (
          <div className="space-y-6">
            {/* Current Statuten */}
            <div className="bg-card border border-border p-6">
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4">
                Aktuelle Statuten
              </h2>
              {activeStatuten ? (
                <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-medium">{activeStatuten.title}</p>
                      <p className="text-sm text-muted-foreground">
                        Hochgeladen: {new Date(activeStatuten.uploaded_at).toLocaleDateString('de-CH')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <a 
                      href={activeStatuten.file_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      <Download className="w-4 h-4" />
                      Öffnen
                    </a>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground italic">Noch keine Statuten hochgeladen</p>
              )}
            </div>

            {/* Upload Form */}
            <div className="bg-card border border-border p-6">
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4">
                Neue Statuten hochladen
              </h2>
              <form onSubmit={handleUploadStatuten} className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                    Titel
                  </label>
                  <Input
                    value={statutenTitle}
                    onChange={(e) => setStatutenTitle(e.target.value)}
                    placeholder="Vereinsstatuten"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                    Beschreibung (optional)
                  </label>
                  <Textarea
                    value={statutenDescription}
                    onChange={(e) => setStatutenDescription(e.target.value)}
                    placeholder="z.B. Revidierte Fassung 2026"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                    PDF-Datei *
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    ref={statutenFileRef}
                    className="w-full p-2 border border-border bg-background"
                  />
                </div>
                <Button type="submit" disabled={uploading} className="rounded-none">
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Statuten hochladen
                </Button>
                <p className="text-xs text-muted-foreground">
                  Hinweis: Das Hochladen neuer Statuten ersetzt die bisherige Version.
                </p>
              </form>
            </div>

            {/* History */}
            {statuten.length > 1 && (
              <div className="bg-card border border-border p-6">
                <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4">
                  Frühere Versionen
                </h2>
                <div className="space-y-2">
                  {statuten.filter(s => !s.is_active).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-muted/50 border border-border">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-muted-foreground">{doc.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(doc.uploaded_at).toLocaleDateString('de-CH')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a 
                          href={doc.file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-muted"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Protokolle Tab */}
        {activeTab === 'protokolle' && (
          <div className="space-y-6">
            {/* Upload Form */}
            <div className="bg-card border border-border p-6">
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4">
                Neues GV-Protokoll hochladen
              </h2>
              <form onSubmit={handleUploadProtokoll} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                      Titel *
                    </label>
                    <Input
                      value={protokollTitle}
                      onChange={(e) => setProtokollTitle(e.target.value)}
                      placeholder="z.B. Protokoll 35. Generalversammlung"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                      Jahr *
                    </label>
                    <Input
                      type="number"
                      value={protokollYear}
                      onChange={(e) => setProtokollYear(parseInt(e.target.value))}
                      min="2000"
                      max="2100"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                      Datum der GV *
                    </label>
                    <Input
                      type="date"
                      value={protokollDate}
                      onChange={(e) => setProtokollDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                      PDF-Datei *
                    </label>
                    <input
                      type="file"
                      accept=".pdf"
                      ref={protokollFileRef}
                      className="w-full p-2 border border-border bg-background"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                    Beschreibung (optional)
                  </label>
                  <Textarea
                    value={protokollDescription}
                    onChange={(e) => setProtokollDescription(e.target.value)}
                    placeholder="Zusätzliche Informationen..."
                    rows={2}
                  />
                </div>
                <Button type="submit" disabled={uploading} className="rounded-none">
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Protokoll hochladen
                </Button>
              </form>
            </div>

            {/* Protokolle List */}
            <div className="bg-card border border-border p-6">
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4">
                Alle GV-Protokolle ({protokolle.filter(p => p.is_active).length})
              </h2>
              {protokolle.filter(p => p.is_active).length > 0 ? (
                <div className="space-y-3">
                  {protokolle
                    .filter(p => p.is_active)
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-4 bg-secondary/30 border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
                            <span className="font-bold text-primary">{doc.year}</span>
                          </div>
                          <div>
                            <p className="font-medium">{doc.title}</p>
                            <p className="text-sm text-muted-foreground">
                              <Calendar className="inline w-3 h-3 mr-1" />
                              {new Date(doc.date).toLocaleDateString('de-CH', { 
                                day: '2-digit', 
                                month: 'long', 
                                year: 'numeric' 
                              })}
                            </p>
                            {doc.description && (
                              <p className="text-xs text-muted-foreground mt-1">{doc.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <a 
                            href={doc.file_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
                          >
                            <Download className="w-4 h-4" />
                            PDF
                          </a>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(doc.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic">Noch keine Protokolle hochgeladen</p>
              )}
            </div>

            {/* Deleted Protokolle */}
            {protokolle.filter(p => !p.is_active).length > 0 && (
              <div className="bg-card border border-border p-6">
                <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4 text-muted-foreground">
                  Gelöschte Protokolle
                </h2>
                <div className="space-y-2">
                  {protokolle
                    .filter(p => !p.is_active)
                    .map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-muted/30 border border-border opacity-60">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-muted-foreground">{doc.year}</span>
                          <span className="text-muted-foreground">{doc.title}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRestore(doc.id)}
                          className="text-primary"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Wiederherstellen
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Jahresberichte Tab */}
        {activeTab === 'jahresberichte' && (
          <YearlyReportTab
            label="Jahresbericht"
            labelPlural="Jahresberichte"
            docs={jahresberichte}
            titleVal={jbTitle}
            setTitle={setJbTitle}
            yearVal={jbYear}
            setYear={setJbYear}
            descVal={jbDescription}
            setDesc={setJbDescription}
            fileRef={jbFileRef}
            uploading={uploading}
            onUpload={(e) => handleUploadYearlyReport(e, 'jahresbericht', 'Jahresbericht', jbTitle, jbYear, jbDescription, jbFileRef, () => { setJbTitle(''); setJbDescription(''); })}
            onDelete={handleDelete}
            onRestore={handleRestore}
          />
        )}

        {/* Finanzberichte Tab */}
        {activeTab === 'finanzberichte' && (
          <YearlyReportTab
            label="Finanzbericht"
            labelPlural="Finanzberichte"
            docs={finanzberichte}
            titleVal={fbTitle}
            setTitle={setFbTitle}
            yearVal={fbYear}
            setYear={setFbYear}
            descVal={fbDescription}
            setDesc={setFbDescription}
            fileRef={fbFileRef}
            uploading={uploading}
            onUpload={(e) => handleUploadYearlyReport(e, 'finanzbericht', 'Finanzbericht', fbTitle, fbYear, fbDescription, fbFileRef, () => { setFbTitle(''); setFbDescription(''); })}
            onDelete={handleDelete}
            onRestore={handleRestore}
          />
        )}

        {/* Downloads / Allgemein Tab */}
        {activeTab === 'allgemein' && (
          <div className="space-y-6">
            {/* Upload Form */}
            <div className="bg-card border border-border p-6">
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4">
                Dokument hochladen
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Allgemeine Dokumente mit frei wählbarem Titel, sichtbar für Mitglieder auf der Dokumente-Seite.
              </p>
              <form onSubmit={handleUploadAllgemein} className="space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">Titel *</label>
                  <Input
                    value={allgTitle}
                    onChange={(e) => setAllgTitle(e.target.value)}
                    placeholder="z.B. Anmeldeformular, Reglement, Rundschreiben..."
                    required
                    data-testid="allgemein-title-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">PDF-Datei *</label>
                  <input
                    type="file" accept=".pdf" ref={allgFileRef}
                    className="w-full p-2 border border-border bg-background"
                    data-testid="allgemein-file-input"
                  />
                </div>
                <Button type="submit" disabled={uploading} className="rounded-none" data-testid="upload-allgemein-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Hochladen
                </Button>
              </form>
            </div>

            {/* Documents List */}
            <div className="bg-card border border-border p-6">
              <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4">
                Alle Downloads ({allgemein.filter(d => d.is_active).length})
              </h2>
              {allgemein.filter(d => d.is_active).length > 0 ? (
                <div className="space-y-3">
                  {allgemein.filter(d => d.is_active).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-4 bg-secondary/30 border border-border">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{doc.title}</p>
                          <p className="text-sm text-muted-foreground">
                            <Calendar className="inline w-3 h-3 mr-1" />
                            {new Date(doc.uploaded_at).toLocaleDateString('de-CH')}
                            <span className="ml-2 text-xs">{doc.filename}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90">
                          <Download className="w-4 h-4" /> PDF
                        </a>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)} className="text-destructive hover:text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic">Noch keine Dokumente hochgeladen</p>
              )}
            </div>

            {/* Deleted */}
            {allgemein.filter(d => !d.is_active).length > 0 && (
              <div className="bg-card border border-border p-6">
                <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4 text-muted-foreground">
                  Gelöschte Dokumente
                </h2>
                <div className="space-y-2">
                  {allgemein.filter(d => !d.is_active).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-muted/30 border border-border opacity-60">
                      <span className="text-muted-foreground">{doc.title}</span>
                      <Button variant="ghost" size="sm" onClick={() => handleRestore(doc.id)} className="text-primary">
                        <RotateCcw className="w-4 h-4 mr-1" /> Wiederherstellen
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}


function YearlyReportTab({ label, labelPlural, docs, titleVal, setTitle, yearVal, setYear, descVal, setDesc, fileRef, uploading, onUpload, onDelete, onRestore }) {
  const activeDocs = docs.filter(d => d.is_active).sort((a, b) => b.year - a.year);
  const deletedDocs = docs.filter(d => !d.is_active);

  return (
    <div className="space-y-6">
      {/* Upload Form */}
      <div className="bg-card border border-border p-6">
        <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4">
          {label} hochladen
        </h2>
        <form onSubmit={onUpload} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                Titel *
              </label>
              <Input
                value={titleVal}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`z.B. ${label} ${yearVal}`}
                required
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
                Jahr *
              </label>
              <Input
                type="number"
                value={yearVal}
                onChange={(e) => setYear(parseInt(e.target.value))}
                min="2000"
                max="2100"
                required
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
              PDF-Datei *
            </label>
            <input
              type="file"
              accept=".pdf"
              ref={fileRef}
              className="w-full p-2 border border-border bg-background"
              data-testid={`${label.toLowerCase()}-file-input`}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
              Beschreibung (optional)
            </label>
            <Textarea
              value={descVal}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Zusätzliche Informationen..."
              rows={2}
            />
          </div>
          <Button type="submit" disabled={uploading} className="rounded-none" data-testid={`upload-${label.toLowerCase()}-btn`}>
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {label} hochladen
          </Button>
        </form>
      </div>

      {/* Documents List */}
      <div className="bg-card border border-border p-6">
        <h2 className="font-['Oswald'] text-xl font-bold uppercase tracking-wider mb-4">
          Alle {labelPlural} ({activeDocs.length})
        </h2>
        {activeDocs.length > 0 ? (
          <div className="space-y-3">
            {activeDocs.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-4 bg-secondary/30 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
                    <span className="font-bold text-primary">{doc.year}</span>
                  </div>
                  <div>
                    <p className="font-medium">{doc.title}</p>
                    <p className="text-sm text-muted-foreground">
                      <Calendar className="inline w-3 h-3 mr-1" />
                      {new Date(doc.uploaded_at).toLocaleDateString('de-CH')}
                    </p>
                    {doc.description && (
                      <p className="text-xs text-muted-foreground mt-1">{doc.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <a 
                    href={doc.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </a>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(doc.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground italic">Noch keine {labelPlural} hochgeladen</p>
        )}
      </div>

      {/* Deleted docs */}
      {deletedDocs.length > 0 && (
        <div className="bg-card border border-border p-6">
          <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider mb-4 text-muted-foreground">
            Gelöschte {labelPlural}
          </h2>
          <div className="space-y-2">
            {deletedDocs.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-muted/30 border border-border opacity-60">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-muted-foreground">{doc.year}</span>
                  <span className="text-muted-foreground">{doc.title}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRestore(doc.id)}
                  className="text-primary"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Wiederherstellen
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
